import React from "react";
import ReactMarkdown from "react-markdown";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

/**
 * Renders a single What's New release entry: title, date, and markdown body.
 * Inline ![]() images in the body are rendered as styled <img> tags.
 */
// Rewrites /whats-new/foo.png → /whats-new/foo-light.png or /whats-new/foo-dark.png
function themedSrc(src, mode) {
  if (!src) return src;
  return src.replace(/(\.[^.]+)$/, `-${mode}$1`);
}

function ReleaseEntry({ entry }) {
  const theme = useTheme();
  const formattedDate = new Date(entry.publishedAt).toLocaleDateString(
    undefined,
    { year: "numeric", month: "long", day: "numeric" },
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight="bold">
        {entry.title}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        {formattedDate}
      </Typography>
      {entry.body && (
        <ReactMarkdown
          components={{
            img: ({ src, alt }) => (
              <Box
                component="img"
                src={themedSrc(src, theme.palette.mode)}
                alt={alt}
                sx={{ maxWidth: "100%", display: "block", my: 2 }}
              />
            ),
            a: ({ href, children }) => {
              const isInternal = href && href.startsWith("/");
              return isInternal ? (
                <MuiLink component={RouterLink} to={href}>
                  {children}
                </MuiLink>
              ) : (
                <MuiLink href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </MuiLink>
              );
            },
            p: ({ children }) => (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {children}
              </Typography>
            ),
            h2: ({ children }) => (
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mt: 2, mb: 0.5 }}
              >
                {children}
              </Typography>
            ),
          }}
        >
          {entry.body}
        </ReactMarkdown>
      )}
    </Box>
  );
}

export default ReleaseEntry;
